/**
 * SSR-Safe Logger Wrapper
 *
 * This is a minimal logger that works during both SSR and client-side rendering.
 * It avoids the module loading issues that createLogger has during Vike pre-rendering.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
    module: string;
    level?: LogLevel;
    enabled?: boolean;
}

interface Logger {
    debug(message: string, data?: Record<string, unknown>): void;
    info(message: string, data?: Record<string, unknown>): void;
    warn(message: string, data?: Record<string, unknown>): void;
    error(message: string, error?: Error | unknown, data?: Record<string, unknown>): void;
    render(data?: Record<string, unknown>): void;
}

// No-op logger fallback
const noopLogger: Logger = {
    debug: () => {},
    info: () => {},
    warn: () => {},
    error: () => {},
    render: () => {}
};

/**
 * Create an SSR-safe logger instance
 * Falls back to console methods, works in both Node and browser
 *
 * IMPORTANT: Safe to call at module level during SSR
 */
function createLogger(options?: LoggerOptions): Logger {
    // Guard against undefined options (shouldn't happen but be defensive)
    if (!options) {
        return noopLogger;
    }

    try {
        const { module, level = 'info', enabled } = options;

        // Determine if logging is enabled
        const isEnabled = enabled !== undefined ? enabled : (
            typeof process !== 'undefined' && process.env?.NODE_ENV === 'development'
        );

        const levels: Record<LogLevel, number> = {
            debug: 0,
            info: 1,
            warn: 2,
            error: 3
        };

        const shouldLog = (logLevel: LogLevel): boolean => {
            if (!isEnabled) return false;
            return levels[logLevel] >= levels[level];
        };

        const formatMessage = (message: string, data?: Record<string, unknown>): string => {
            try {
                const timestamp = new Date().toISOString();
                const dataString = data ? ` ${JSON.stringify(data)}` : '';
                return `[${timestamp}] [${module}] ${message}${dataString}`;
            } catch {
                return `[${module}] ${message}`;
            }
        };

        return {
            debug(message: string, data?: Record<string, unknown>): void {
                try {
                    if (shouldLog('debug') && typeof console !== 'undefined') {
                        console.debug(formatMessage(message, data));
                    }
                } catch {
                    // Silently fail
                }
            },

            info(message: string, data?: Record<string, unknown>): void {
                try {
                    if (shouldLog('info') && typeof console !== 'undefined') {
                        console.info(formatMessage(message, data));
                    }
                } catch {
                    // Silently fail
                }
            },

            warn(message: string, data?: Record<string, unknown>): void {
                try {
                    if (shouldLog('warn') && typeof console !== 'undefined') {
                        console.warn(formatMessage(message, data));
                    }
                } catch {
                    // Silently fail
                }
            },

            error(message: string, error?: Error | unknown, data?: Record<string, unknown>): void {
                try {
                    if (shouldLog('error') && typeof console !== 'undefined') {
                        let errorData: Record<string, unknown> = {};

                        if (error instanceof Error) {
                            errorData = {
                                name: error.name,
                                message: error.message,
                                stack: error.stack
                            };
                        } else if (error) {
                            errorData = { error: String(error) };
                        }

                        const combinedData = { ...errorData, ...data };
                        console.error(formatMessage(message, combinedData));
                    }
                } catch {
                    // Silently fail
                }
            },

            render(_data?: Record<string, unknown>): void {
                // No-op for SSR safety - render logging not critical
            }
        };
    } catch {
        // If anything goes wrong, return noop logger
        return noopLogger;
    }
}

// Export both as default and named for maximum compatibility
export { createLogger };
export default createLogger;
